import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Utensils, HandCoins, Handshake, Gift, Phone, Mail, MapPin } from 'lucide-react';
import { HERO_SLIDES, DEAL_CARDS, GALLERY_IMAGES, OPENING_HOURS, HOURS_IMAGE, YT_VIDEO_ID, WRAPS_IMAGE, ABOUT_IMAGE, WHY_IMAGE, WHY_CHOOSE_US, ORDER_URL, CONTACT } from '../mock';
import ReviewsSection from '../components/ReviewsSection';
import api, { resolveUrl } from '../lib/api';
import { Link } from 'react-router-dom';

const ICONS = { Utensils, HandCoins, Handshake, Gift };
const DEFAULT_SIGNATURE_BG = 'https://wrappus.com/wp-content/uploads/2025/12/IMG_3699-scaled.jpg';

export default function Home() {
  const [slide, setSlide] = useState(0);
  const [deals, setDeals] = useState(DEAL_CARDS);
  const [hours, setHours] = useState(OPENING_HOURS);
  const [heroSlides, setHeroSlides] = useState(HERO_SLIDES);
  const [aboutImage, setAboutImage] = useState(ABOUT_IMAGE);
  const [hoursImage, setHoursImage] = useState(HOURS_IMAGE);
  const [whyImage, setWhyImage] = useState(WHY_IMAGE);
  const [signatureBgImage, setSignatureBgImage] = useState(DEFAULT_SIGNATURE_BG);
  const [galleryImages, setGalleryImages] = useState(GALLERY_IMAGES);

  useEffect(() => {
    api.get('/deals').then((r) => {
      if (r.data?.length) setDeals(r.data.map((d) => ({ tag: d.subtitle, title: d.title, desc: d.description, cta: d.ctaLabel, ctaUrl: d.ctaUrl, image: d.image })));
    }).catch(() => {});
    api.get('/hours').then((r) => { if (r.data?.length) setHours(r.data.map((h) => ({ day: h.day, time: h.time }))); }).catch(() => {});
    api.get('/site-settings').then((r) => {
      const s = r.data || {};
      if (Array.isArray(s.heroSlides) && s.heroSlides.length) {
        // Filter by optional startAt/endAt window (inclusive dates, in local timezone).
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const active = s.heroSlides.filter((sl) => {
          const start = sl.startAt ? new Date(sl.startAt + 'T00:00:00') : null;
          const end = sl.endAt ? new Date(sl.endAt + 'T23:59:59') : null;
          if (start && today < start) return false;
          if (end && today > end) return false;
          return true;
        });
        if (active.length) setHeroSlides(active);
      }
      if (s.aboutImage) setAboutImage(resolveUrl(s.aboutImage));
      if (s.hoursImage) setHoursImage(resolveUrl(s.hoursImage));
      if (s.whyImage) setWhyImage(resolveUrl(s.whyImage));
      if (s.signatureBgImage) setSignatureBgImage(resolveUrl(s.signatureBgImage));
      if (Array.isArray(s.galleryImages) && s.galleryImages.length) setGalleryImages(s.galleryImages.map(resolveUrl));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const t = setInterval(() => setSlide(s => (s + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  const s = heroSlides[Math.min(slide, heroSlides.length - 1)] || heroSlides[0];

  return (
    <main>
      {/* HERO SLIDER */}
      <section className="relative h-screen min-h-[640px] overflow-hidden bg-[#0d0d0f]">
        <div key={slide} className="absolute inset-0 fade-up">
          <img src={resolveUrl(s.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="hero-diagonal absolute inset-0" />
        </div>

        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto w-full px-6 lg:px-24 pt-20">
            <div key={slide + '-c'} className="max-w-2xl fade-up">
              <div className="inline-flex items-center gap-2 text-[#ED2E2E] font-bold tracking-widest text-sm uppercase mb-4">
                {s.tag} {s.emoji === 'party' ? '' : s.emoji === 'canada' ? '🇨🇦' : ''}
              </div>
              <h1 className="font-heading font-black text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] uppercase whitespace-pre-line mb-6">
                {s.title}
              </h1>
              <p className="text-white/85 text-lg mb-8 max-w-xl whitespace-pre-line">{s.subtitle}</p>
              <Link to={s.ctaTo || '/menu'} className="btn-wrappus text-base">
                {s.cta}
                <span className="arrow"><ArrowRight size={16} strokeWidth={2.5} /></span>
              </Link>
            </div>
          </div>
        </div>

        {/* Slide controls */}
        <button onClick={() => setSlide((slide - 1 + heroSlides.length) % heroSlides.length)} className="absolute right-24 bottom-8 z-20 w-11 h-11 rounded-full bg-white/10 hover:bg-[#ED2E2E] backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors">
          <ChevronLeft size={20} />
        </button>
        <button onClick={() => setSlide((slide + 1) % heroSlides.length)} className="absolute right-10 bottom-8 z-20 w-11 h-11 rounded-full bg-white/10 hover:bg-[#ED2E2E] backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors">
          <ChevronRight size={20} />
        </button>

        {/* Slide dots */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-8 z-20 flex gap-2">
          {heroSlides.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? 'bg-[#ED2E2E] w-8' : 'bg-white/40 w-4'}`} />
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 flex-col items-center gap-2">
          <div className="w-px h-24 bg-white/40" />
          <span className="scroll-me">SCROLL ME</span>
          <div className="w-px h-24 bg-white/40" />
        </div>
      </section>

      {/* DEAL CARDS */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-6">
          {deals.map((d, i) => (
            <div key={i} className="bg-[#FDF3EC] rounded-3xl p-8 flex items-center gap-4 hover:shadow-xl transition-shadow group">
              <div className="flex-1">
                <div className="eyebrow mb-2">{d.tag}</div>
                <h3 className="section-title text-2xl md:text-3xl mb-3">{d.title}</h3>
                <p className="text-gray-600 text-sm mb-5">{d.desc}</p>
                <Link to={d.ctaUrl || '/menu'} className="btn-wrappus text-sm">
                  {d.cta} <ArrowRight size={14} />
                </Link>
              </div>
              {d.image && <img src={d.image} alt={d.title} className="w-40 md:w-52 object-contain group-hover:scale-105 transition-transform" />}
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT WRAPPUS */}
      <section className="py-20 bg-[#FDF3EC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow mb-3">About Wrappus</div>
            <h2 className="section-title text-3xl md:text-5xl mb-6">Born in Port Coquitlam. Built to Go Places.</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Located on <strong>3353 Coast Meridian Rd, Port Coquitlam, BC V3B 3N6</strong>, Wrappus is your new go-to for satisfying donairs, bold sauces, and fast service. While we’re not your traditional donair joint, we’re all about bringing fresh vibes to a classic favorite. One location for now — but big flavors (and franchise plans) ahead.
            </p>
            <a href="/about" className="btn-wrappus text-sm">Learn More About Us <ArrowRight size={14} /></a>
          </div>
          <div className="relative">
            <img src={aboutImage} alt="About" className="rounded-3xl w-full object-cover shadow-xl" />
            <div className="absolute -bottom-6 -left-6 bg-[#ED2E2E] text-white rounded-2xl px-6 py-4 shadow-2xl hidden md:block">
              <div className="text-3xl font-black">7.99$</div>
              <div className="text-xs uppercase tracking-wider">Daily Deal</div>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY MARQUEE */}
      <section className="py-8 bg-white overflow-hidden">
        <div className="flex gallery-scroll gap-4" style={{ width: 'max-content' }}>
          {[...galleryImages, ...galleryImages, ...galleryImages].map((img, i) => (
            <img key={i} src={img} alt="food" className="w-64 h-64 md:w-80 md:h-80 object-cover rounded-3xl" />
          ))}
        </div>
      </section>

      {/* OPENING HOURS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img src={hoursImage} alt="Hours" className="rounded-3xl w-full object-cover" />
          </div>
          <div>
            <h2 className="section-title text-3xl md:text-5xl mb-4">Our Opening Hours</h2>
            <p className="text-gray-600 mb-8">Whether you’re placing a large group order, asking about our ingredients, or just want to say hi — we’d love to hear from you!</p>
            <div className="rounded-2xl overflow-hidden border border-gray-100 hours-table">
              {hours.map((h) => (
                <div key={h.day} className="flex items-center justify-between px-6 py-3 border-b border-gray-100 last:border-b-0">
                  <div className="font-semibold text-[#0F1C2E]">{h.day}</div>
                  <div className="font-semibold text-[#0F1C2E]">{h.time}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE SECTION (image background) */}
      <section className="relative py-32 bg-[#0F1C2E] overflow-hidden">
        <img src={signatureBgImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1C2E]/85 via-[#0F1C2E]/60 to-[#0F1C2E]/40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 flex flex-col items-center text-center">
          <div className="eyebrow mb-3">Healthy, Tasty & Always Fresh.</div>
          <h2 className="section-title text-white text-3xl md:text-5xl lg:text-6xl max-w-3xl mb-6">WE SERVE THE BEST WRAPS</h2>
          <p className="text-white/80 max-w-2xl mb-8">Freshly made every day with hand-picked ingredients, bold Wrappus sauces, and generous portions — because a great wrap deserves to be memorable.</p>
          <Link to="/menu" className="btn-wrappus text-base">
            Explore Menu <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center">
          <img src={whyImage} alt="Why choose" className="rounded-3xl w-full object-cover shadow-lg" />
          <div>
            <div className="eyebrow mb-3">Why Choose Us?</div>
            <h2 className="section-title text-3xl md:text-5xl mb-4">Best Quality Food, Honest Pricing & Real Flavors</h2>
            <p className="text-gray-600 mb-8">At Wrappus Donair, we believe everyone deserves a satisfying meal that feels like home — without breaking the bank. Here’s why locals keep coming back:</p>
            <div className="space-y-5">
              {WHY_CHOOSE_US.map((w) => {
                const Icon = ICONS[w.icon];
                return (
                  <div key={w.title} className="flex gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-[#ED2E2E] text-white flex items-center justify-center shrink-0 shadow-md">
                      <Icon size={22} />
                    </div>
                    <div>
                      <h4 className="text-[#F5A623] font-bold text-lg mb-1">{w.title}</h4>
                      <p className="text-gray-600 text-sm">{w.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <ReviewsSection />

      {/* CONTACT INFO BAR */}
      <section className="py-14 bg-[#FDF3EC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-6">
          <a href={`tel:${CONTACT.phoneRaw}`} className="bg-white rounded-2xl p-6 flex items-center gap-4 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-[#ED2E2E] text-white flex items-center justify-center"><Phone size={20} /></div>
            <div><div className="text-[#ED2E2E] font-bold">Phone</div><div className="font-semibold text-[#0F1C2E]">{CONTACT.phone}</div></div>
          </a>
          <a href={`mailto:${CONTACT.email}`} className="bg-white rounded-2xl p-6 flex items-center gap-4 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-[#ED2E2E] text-white flex items-center justify-center"><Mail size={20} /></div>
            <div><div className="text-[#ED2E2E] font-bold">Email</div><div className="font-semibold text-[#0F1C2E]">{CONTACT.email}</div></div>
          </a>
          <a href={CONTACT.mapUrl} target="_blank" rel="noopener noreferrer" className="bg-white rounded-2xl p-6 flex items-center gap-4 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-[#ED2E2E] text-white flex items-center justify-center"><MapPin size={20} /></div>
            <div><div className="text-[#ED2E2E] font-bold">Location</div><div className="font-semibold text-[#0F1C2E] text-sm">{CONTACT.address}</div></div>
          </a>
        </div>
      </section>
    </main>
  );
}
